import AWS from 'aws-sdk';

// Configure AWS SDK
const s3 = new AWS.S3({
  accessKeyId: 'AKIA2K3BQ7ARP3CLPKTR',
  secretAccessKey: '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',
  region: 'ap-south-1'
});

/**
 * Upload original user image (uploaded/captured) to S3 bucket
 * @param {File|Blob} imageFile - The original image file from upload/camera/selfie
 * @param {Object} userData - User data for filename generation
 * @param {string} imageType - Type of image: 'upload', 'camera', or 'selfie'
 * @returns {Promise<{success: boolean, url?: string, fileName?: string, error?: string}>}
 */
export const uploadOriginalImageToS3 = async (imageFile, userData = {}, imageType = 'upload') => {
  try {
    console.log('📤 Uploading original image to S3...', imageType);
    
    // Generate a unique filename for original image
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const userName = userData.name ? userData.name.replace(/[^a-zA-Z0-9]/g, '_') : 'user';
    const fileName = `user-uploads/original_${userName}_${timestamp}_${imageType}.jpg`;
    
    // Convert File/Blob to proper format if needed
    let imageBody = imageFile;
    let contentType = 'image/jpeg';
    
    // Handle different input types
    if (imageFile instanceof File) {
      contentType = imageFile.type || 'image/jpeg';
      imageBody = imageFile;
    } else if (typeof imageFile === 'string' && imageFile.startsWith('data:')) {
      // Handle data URLs (from camera capture)
      const response = await fetch(imageFile);
      imageBody = await response.blob();
      contentType = 'image/jpeg';
    } else if (typeof imageFile === 'string' && imageFile.startsWith('blob:')) {
      // Handle blob URLs
      const response = await fetch(imageFile);
      imageBody = await response.blob();
      contentType = 'image/jpeg';
    }
    
    // Upload parameters for original image
    const uploadParams = {
      Bucket: 'ltfuserimages',
      Key: fileName,
      Body: imageBody,
      ContentType: contentType,
      ServerSideEncryption: 'AES256',
      Metadata: {
        'user-name': userData.name || 'Unknown',
        'company-name': userData.companyName || 'Unknown',
        'industry': userData.industry || 'Unknown',
        'phone-number': userData.phoneNumber || 'Unknown',
        'image-type': imageType,
        'upload-stage': 'original',
        'upload-timestamp': new Date().toISOString()
      }
    };
    
    // Upload to S3
    const result = await s3.upload(uploadParams).promise();
    
    console.log('✅ Original image uploaded successfully to S3:', result.Location);
    
    return {
      success: true,
      url: result.Location,
      fileName: fileName,
      imageType: imageType
    };
    
  } catch (error) {
    console.error('❌ Error uploading original image to S3:', error);
    
    return {
      success: false,
      error: error.message,
      imageType: imageType
    };
  }
};

/**
 * Upload cropped preview image to S3 bucket
 * @param {string} croppedImageUrl - The cropped image URL (blob or data URL)
 * @param {Object} userData - User data for filename generation
 * @param {Object} cropData - Crop information for metadata
 * @returns {Promise<{success: boolean, url?: string, fileName?: string, error?: string}>}
 */
export const uploadCroppedPreviewToS3 = async (croppedImageUrl, userData = {}, cropData = {}) => {
  try {
    console.log('📤 Uploading cropped preview to S3...');
    
    // Convert cropped image URL to blob
    const response = await fetch(croppedImageUrl);
    const blob = await response.blob();
    
    // Generate a unique filename for cropped preview
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const userName = userData.name ? userData.name.replace(/[^a-zA-Z0-9]/g, '_') : 'user';
    const fileName = `cropped-previews/cropped_${userName}_${timestamp}.jpg`;
    
    // Upload parameters for cropped preview
    const uploadParams = {
      Bucket: 'ltfuserimages',
      Key: fileName,
      Body: blob,
      ContentType: 'image/jpeg',
      ServerSideEncryption: 'AES256',
      Metadata: {
        'user-name': userData.name || 'Unknown',
        'company-name': userData.companyName || 'Unknown',
        'industry': userData.industry || 'Unknown',
        'phone-number': userData.phoneNumber || 'Unknown',
        'upload-stage': 'cropped-preview',
        'upload-timestamp': new Date().toISOString(),
        // Crop metadata
        'crop-aspect-ratio': cropData.aspectRatio ? cropData.aspectRatio.toString() : '0.56',
        'crop-width-percentage': cropData.widthPercentage ? cropData.widthPercentage.toString() : '100',
        'crop-height-percentage': cropData.heightPercentage ? cropData.heightPercentage.toString() : '100',
        'is-selfie-mode': userData.isSelfieMode ? 'true' : 'false'
      }
    };
    
    // Upload to S3
    const result = await s3.upload(uploadParams).promise();
    
    console.log('✅ Cropped preview uploaded successfully to S3:', result.Location);
    
    return {
      success: true,
      url: result.Location,
      fileName: fileName
    };
    
  } catch (error) {
    console.error('❌ Error uploading cropped preview to S3:', error);
    
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Upload a poster image to S3 bucket
 * @param {string} dataUrl - The data URL of the poster image (canvas.toDataURL())
 * @param {Object} userData - User data for filename generation
 * @returns {Promise<{success: boolean, url?: string, error?: string}>}
 */
export const uploadPosterToS3 = async (dataUrl, userData = {}) => {
  try {
    // Convert data URL to blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    
    // Generate a unique filename
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const userName = userData.name ? userData.name.replace(/[^a-zA-Z0-9]/g, '_') : 'user';
    const fileName = `poster_${userName}_${timestamp}.png`;
    
    // Upload parameters
    const uploadParams = {
      Bucket: 'finalposters',
      Key: fileName,
      Body: blob,
      ContentType: 'image/png',
      ServerSideEncryption: 'AES256', // Optional: encrypt the file at rest
      Metadata: {
        'user-name': userData.name || 'Unknown',
        'company-name': userData.companyName || 'Unknown',
        'industry': userData.industry || 'Unknown',
        'upload-timestamp': new Date().toISOString()
      }
    };

    console.log('Uploading poster to S3...', fileName);
    
    // Upload to S3
    const result = await s3.upload(uploadParams).promise();
    
    console.log('Poster uploaded successfully to S3:', result.Location);
    
    return {
      success: true,
      url: result.Location,
      fileName: fileName
    };
    
  } catch (error) {
    console.error('Error uploading poster to S3:', error);
    
    return {
      success: false,
      error: error.message
    };
  }
};
